import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import './glass.css';
import emailjs from '@emailjs/browser';

import HeaderNav from './components/HeaderNav';
import HeroSection from './components/HeroSection';
import AboutUsSection from './components/AboutUsSection';
import StrategicTiersSection from './components/StrategicTiersSection';
import PracticePortfolioCarousel from './components/PracticePortfolioCarousel';
import TierDetailModal from './components/TierDetailModal';
import ServiceDetailModal from './components/ServiceDetailModal';
import InsightDetailModal from './components/InsightDetailModal';
import FooterSection from './components/FooterSection';
import { LATEST_INSIGHTS } from './data/insightsData';
import { STITCH_SERVICES, PRACTICE_CATEGORIES, mapServiceToCategoryAndService } from './data/stitchData';

function App() {
  // Theme state: defaults automatically to device setting; respects manual override when clicked
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    const manualTheme = localStorage.getItem('theme_manual');
    if (manualTheme) return manualTheme === 'dark';
    return typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
  });

  // Keep HTML class in sync
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Real-time listener: automatically apply device theme changes unless manually overridden
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleDeviceThemeChange = (e) => {
      if (!localStorage.getItem('theme_manual')) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleDeviceThemeChange);
    return () => mediaQuery.removeEventListener('change', handleDeviceThemeChange);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem('theme_manual', next ? 'dark' : 'light');
      localStorage.setItem('theme', next ? 'dark' : 'light');
      return next;
    });
  };

  // Modal states for Stitch interactive components
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [activeServiceSelection, setActiveServiceSelection] = useState(null);

  const handleSelectService = (serviceId) => {
    const srv = STITCH_SERVICES.find((s) => s.id === serviceId);
    if (srv) {
      setActiveServiceSelection({ id: serviceId, ts: Date.now() });
      const el = document.getElementById('services');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
      setSelectedService(srv);
    }
  };

  // Email Contact Form State/Refs
  const contactFormRef = useRef();
  const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState("");
  const [selectedSpecificService, setSelectedSpecificService] = useState("");
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState("");
  const WORKER_URL = process.env.REACT_APP_WORKER_URL ? process.env.REACT_APP_WORKER_URL.replace(/\/+$/, '') : '';

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsContactSubmitting(true);
    setContactError("");

    // If Cloudflare Worker is configured, dispatch through Resend (zero watermarks)
    if (WORKER_URL) {
      try {
        const formData = new FormData(contactFormRef.current);
        const payload = Object.fromEntries(formData.entries());
        const res = await fetch(`${WORKER_URL}/api/send-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to submit inquiry');
        }
        setIsContactSubmitting(false);
        setContactSuccess(true);
        setSelectedPrimaryCategory("");
        setSelectedSpecificService("");
        contactFormRef.current.reset();
        return;
      } catch (err) {
        console.warn("Worker submission failed, falling back to EmailJS:", err);
      }
    }

    // Default EmailJS fallback
    emailjs.sendForm(
      'service_48e4vye',
      'template_se6ztzm',
      contactFormRef.current,
      'zHRe6ml9RiD2An7oC'
    )
    .then(() => {
      setIsContactSubmitting(false);
      setContactSuccess(true);
      setSelectedPrimaryCategory("");
      setSelectedSpecificService("");
      contactFormRef.current.reset();
    }, () => {
      setIsContactSubmitting(false);
      setContactError("Sorry, something went wrong. Please try again later.");
    });
  };

  // State for toggling all resource cards
  const [showAllBlogs, setShowAllBlogs] = useState(false);

  // Executive White Papers lead-gen gating
  const [whitePapersEmail, setWhitePapersEmail] = useState("");
  const [whitePapersAccess, setWhitePapersAccess] = useState(false);
  const [whitePapersError, setWhitePapersError] = useState("");
  const [whitePapersEmailSending, setWhitePapersEmailSending] = useState(false);
  const [whitePapersEmailError, setWhitePapersEmailError] = useState("");
  const [whitePapersEmailModal, setWhitePapersEmailModal] = useState(false);

  const handleWhitePapersSubmit = (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(whitePapersEmail)) {
      setWhitePapersError("Please enter a valid email address.");
      return;
    }
    setWhitePapersError("");
    setWhitePapersAccess(true);
  };

  const handleDownloadWhitePapers = () => {
    const link = document.createElement('a');
    link.href = '/white-papers-report.pdf';
    link.download = 'NFBG-Scorpio-Executive-White-Papers.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSendWhitePapersEmail = async () => {
    setWhitePapersEmailSending(true);
    setWhitePapersEmailError("");
    try {
      // 1. If Cloudflare Worker is configured, dispatch with real PDF attachment via Resend
      if (WORKER_URL) {
        const res = await fetch(`${WORKER_URL}/api/send-whitepaper`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: whitePapersEmail })
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to dispatch email');
        }
      } else {
        // Fallback: Notify via client-side EmailJS
        await emailjs.send(
          'service_48e4vye',
          'template_se6ztzm',
          {
            full_name: 'Executive White Papers Subscriber',
            user_email: whitePapersEmail,
            organization: 'Executive White Papers Request',
            service_of_interest: 'Executive White Papers Report',
            message: `Subscriber requested Executive White Papers report for: ${whitePapersEmail}`
          },
          'zHRe6ml9RiD2An7oC'
        );
      }
      // Instant PDF download for seamless executive experience
      handleDownloadWhitePapers();
      setWhitePapersEmailModal(true);
    } catch (err) {
      console.warn('Dispatch note:', err);
      // Gracefully fall back to instant PDF download so user is never blocked
      handleDownloadWhitePapers();
      setWhitePapersEmailModal(true);
    } finally {
      setWhitePapersEmailSending(false);
    }
  };

  const scrollToContact = (serviceTitle) => {
    if (typeof serviceTitle === 'string' && serviceTitle) {
      const mapped = mapServiceToCategoryAndService(serviceTitle);
      setSelectedPrimaryCategory(mapped.category);
      setSelectedSpecificService(mapped.service);
    }
    const el = document.getElementById('contact');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="font-sans bg-surface dark:bg-clinical-slate-900 text-on-surface dark:text-slate-100 min-h-screen relative overflow-x-hidden selection:bg-primary-fixed selection:text-on-primary-fixed transition-colors duration-300">
      {/* Ambient Glass Glow Circles */}
      <div className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden z-0">
        <div className="absolute -top-32 -left-32 w-[550px] h-[550px] rounded-full bg-primary/10 dark:bg-primary/15 blur-3xl pointer-events-none"></div>
        <div className="absolute top-1/3 -right-48 w-[550px] h-[550px] rounded-full bg-tertiary-fixed/20 dark:bg-secondary/15 blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-10 left-1/4 w-[400px] h-[400px] rounded-full bg-accent-teal/10 dark:bg-accent-teal/15 blur-3xl pointer-events-none"></div>
      </div>

      {/* Modern Liquid Glass Header with Centered Nav & Larger Logo */}
      <HeaderNav
        onScheduleClick={scrollToContact}
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
        onSelectService={handleSelectService}
      />

      <main className="w-full pt-20 md:pt-24 relative z-10">
        {/* Hero Section */}
        <HeroSection onScheduleClick={scrollToContact} />

        {/* About Us Section: Narrative + 3 Core Differentiators + "NFBG Scorpio Trusted" with Logo */}
        <AboutUsSection onScheduleClick={scrollToContact} />

        {/* Strategic Tiers Architecture Section (3 Tiers Slider + Bento Grid) */}
        <StrategicTiersSection
          onInspectTier={(tierId) => setSelectedTierId(tierId)}
          onScheduleClick={scrollToContact}
        />

        {/* Interactive Practice Portfolio Carousel (Exactly 8 Aligned Services) */}
        <PracticePortfolioCarousel
          activeServiceSelection={activeServiceSelection}
          onOpenService={(service) => setSelectedService(service)}
        />

        {/* Executive White Papers Section */}
        <section id="white-papers" className="py-16 bg-surface-container-low/70 dark:bg-clinical-slate-800/40">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-3">Executive White Papers</h2>
            <p className="text-gray-600 dark:text-slate-300 text-center max-w-2xl mx-auto mb-10">
              Evidence-based frameworks and research for C-suite healthcare leadership and medical executive governance.
            </p>

            <div className="max-w-4xl mx-auto">
              <div className="bg-white dark:bg-clinical-slate-900 rounded-3xl shadow-sm border border-gray-200 dark:border-gray-800 p-8 sm:p-10 flex flex-col items-center">
                <h3 className="text-xl sm:text-2xl font-bold text-primary dark:text-primary-fixed mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary dark:text-primary-fixed text-[28px]">article</span>
                  <span>Key Insights at a Glance</span>
                </h3>
                <ul className="text-left mb-6 space-y-2 text-sm sm:text-base text-gray-700 dark:text-slate-300">
                  <li><strong className="text-primary dark:text-primary-fixed">1. Strategic Leadership:</strong> Frameworks for aligning clinical and administrative priorities to drive performance and engagement.</li>
                  <li><strong className="text-primary dark:text-primary-fixed">2. Mediation &amp; Conflict Mastery:</strong> Early conflict identification, expert neutral mediation, and conflict competency training for sustainable collaboration.</li>
                  <li><strong className="text-primary dark:text-primary-fixed">3. System Design for Agility:</strong> Actionable models for transforming healthcare structures to enable innovation, efficiency, and patient-centric care.</li>
                </ul>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 w-full">
                  <div className="bg-surface-container-low dark:bg-clinical-slate-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 flex flex-col items-center text-center">
                    <span className="material-symbols-outlined text-primary dark:text-primary-fixed text-[32px] mb-1">handshake</span>
                    <span className="font-bold text-gray-900 dark:text-white text-sm">Reduce Friction</span>
                    <span className="text-gray-500 dark:text-slate-400 text-xs">Break down silos and foster trust</span>
                  </div>
                  <div className="bg-surface-container-low dark:bg-clinical-slate-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 flex flex-col items-center text-center">
                    <span className="material-symbols-outlined text-accent-teal text-[32px] mb-1">trending_up</span>
                    <span className="font-bold text-gray-900 dark:text-white text-sm">Drive Results</span>
                    <span className="text-gray-500 dark:text-slate-400 text-xs">Unlock high-performing teams</span>
                  </div>
                  <div className="bg-surface-container-low dark:bg-clinical-slate-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-4 flex flex-col items-center text-center">
                    <span className="material-symbols-outlined text-secondary text-[32px] mb-1">speed</span>
                    <span className="font-bold text-gray-900 dark:text-white text-sm">Accelerate Change</span>
                    <span className="text-gray-500 dark:text-slate-400 text-xs">Transform with confidence</span>
                  </div>
                </div>

                {!whitePapersAccess ? (
                  <form className="w-full max-w-md mx-auto mt-4 flex flex-col items-center space-y-3" onSubmit={handleWhitePapersSubmit}>
                    <label htmlFor="whitePapersEmail" className="block text-gray-700 dark:text-slate-200 font-semibold text-sm">
                      Access the Full Executive White Papers Report
                    </label>
                    <input
                      type="email"
                      id="whitePapersEmail"
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 bg-white dark:bg-clinical-slate-800 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      placeholder="Enter your executive email address"
                      value={whitePapersEmail}
                      onChange={e => setWhitePapersEmail(e.target.value)}
                      required
                    />
                    <button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary-container text-white font-bold py-2.5 px-6 rounded-xl shadow-md transition-all active:scale-95 text-sm"
                    >
                      Get Instant Access
                    </button>
                    {whitePapersError && <span className="text-red-600 text-xs">{whitePapersError}</span>}
                  </form>
                ) : (
                  <div className="w-full max-w-md mx-auto mt-4 flex flex-col items-center space-y-3">
                    <p className="text-green-700 dark:text-green-400 font-semibold text-sm">Thank you! How would you like to receive the full PDF report?</p>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        className="bg-primary hover:bg-primary-container text-white font-bold py-2 px-5 rounded-xl shadow-sm text-sm"
                        onClick={handleDownloadWhitePapers}
                      >
                        Download PDF
                      </button>
                      <button
                        type="button"
                        className="bg-secondary hover:bg-primary-container text-white font-bold py-2 px-5 rounded-xl shadow-sm text-sm"
                        onClick={handleSendWhitePapersEmail}
                        disabled={whitePapersEmailSending}
                      >
                        {whitePapersEmailSending ? "Sending..." : "Email to Me"}
                      </button>
                    </div>
                    {whitePapersEmailError && <span className="text-red-600 text-xs">{whitePapersEmailError}</span>}
                  </div>
                )}
              </div>
            </div>

            {/* White Paper Confirmation Modal */}
            {whitePapersEmailModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-clinical-slate-900 rounded-3xl p-8 max-w-md w-full relative flex flex-col items-center text-center shadow-2xl border border-gray-200 dark:border-gray-800">
                  <button
                    type="button"
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-white"
                    onClick={() => setWhitePapersEmailModal(false)}
                  >
                    <span className="material-symbols-outlined text-[24px]">close</span>
                  </button>
                  <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-[48px] mb-3">check_circle</span>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Report Dispatched</h3>
                  <p className="text-gray-600 dark:text-slate-300 text-sm mb-6">
                    The Executive White Papers PDF has been dispatched to your email address. You may also download a copy immediately below.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <button
                      type="button"
                      className="flex-1 bg-primary hover:bg-primary-container text-white font-bold py-2.5 px-5 rounded-xl text-sm shadow-sm transition-all"
                      onClick={handleDownloadWhitePapers}
                    >
                      Download PDF Copy
                    </button>
                    <button
                      type="button"
                      className="flex-1 bg-gray-100 dark:bg-clinical-slate-800 hover:bg-gray-200 dark:hover:bg-clinical-slate-700 text-gray-700 dark:text-gray-200 font-semibold py-2.5 px-5 rounded-xl text-sm transition-all"
                      onClick={() => setWhitePapersEmailModal(false)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Latest Insights Section */}
        <section id="latest-insights" className="py-16 bg-white dark:bg-clinical-slate-900">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-3">Latest Insights</h2>
            <p className="text-gray-600 dark:text-slate-300 text-center max-w-2xl mx-auto mb-12">
              Expert perspectives on healthcare leadership, mediation, and organizational resilience.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {(showAllBlogs ? LATEST_INSIGHTS : LATEST_INSIGHTS.slice(0, 3)).map((insight) => (
                <div
                  key={insight.id}
                  className="bg-white dark:bg-clinical-slate-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md hover:border-primary/40 dark:hover:border-primary/40 transition-all flex flex-col overflow-hidden group"
                >
                  <div className="relative w-full h-52 overflow-hidden bg-gray-900 shrink-0">
                    <img
                      src={insight.image}
                      alt={insight.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 dark:bg-clinical-slate-900/90 backdrop-blur-md text-primary dark:text-primary-fixed text-[11px] font-bold uppercase tracking-wider shadow-xs">
                      {insight.category}
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 dark:text-gray-500 mb-2">
                        <span className="material-symbols-outlined text-[15px]">schedule</span>
                        <span>{insight.readTime}</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-snug line-clamp-2 mb-3">
                        {insight.title}
                      </h3>
                      <p className="text-gray-600 dark:text-slate-300 text-sm line-clamp-3 leading-relaxed mb-4">
                        {insight.summary}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-gray-100 dark:border-gray-800/80">
                      <button
                        type="button"
                        onClick={() => setSelectedInsight(insight)}
                        className="inline-flex items-center gap-1.5 text-primary dark:text-primary-fixed hover:text-primary-container dark:hover:text-white font-bold text-sm transition-colors group-hover:translate-x-0.5 duration-200"
                      >
                        <span>Read Full Briefing</span>
                        <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <button
                type="button"
                onClick={() => setShowAllBlogs(!showAllBlogs)}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-slate-200 font-semibold text-sm hover:bg-gray-100 dark:hover:bg-clinical-slate-800 transition-all shadow-xs"
              >
                <span>{showAllBlogs ? "Show Fewer Resources" : "View All Executive Resources"}</span>
                <span className="material-symbols-outlined text-[18px]">
                  {showAllBlogs ? "expand_less" : "expand_more"}
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* Contact & Consultation Form Section */}
        <section id="contact" className="py-20 bg-surface-container-low/60 dark:bg-clinical-slate-800/30 border-t border-gray-200 dark:border-gray-800">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto bg-white dark:bg-clinical-slate-900 rounded-3xl shadow-lg border border-gray-200 dark:border-gray-800 p-8 sm:p-12">
              <div className="text-center space-y-2 mb-10">
                <span className="text-xs uppercase font-bold text-primary dark:text-primary-fixed tracking-wider">
                  Confidential Inquiry
                </span>
                <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                  Initiate an Executive Consultation
                </h2>
                <p className="text-gray-600 dark:text-slate-300 text-sm sm:text-base max-w-xl mx-auto">
                  All preliminary inquiries operate under strict statutory privilege and confidentiality covenants.
                </p>
              </div>

              <form className="space-y-6" onSubmit={handleContactSubmit} ref={contactFormRef}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="full_name" className="block text-gray-700 dark:text-slate-200 text-sm font-semibold mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-clinical-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      placeholder="e.g. Dr. Sarah Jenkins, CMO"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="user_email" className="block text-gray-700 dark:text-slate-200 text-sm font-semibold mb-2">
                      Official / Executive Email
                    </label>
                    <input
                      type="email"
                      id="user_email"
                      name="user_email"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-clinical-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      placeholder="sjenkins@healthsystem.org"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="organization" className="block text-gray-700 dark:text-slate-200 text-sm font-semibold mb-2">
                    Health System / Physician Group / Organization
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-clinical-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    placeholder="Regional Health System"
                    required
                  />
                </div>

                {/* 2-Step Cascading Selection: 5 Main Categories First, Then Specific Focus Protocols */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="primary_category" className="block text-gray-700 dark:text-slate-200 text-sm font-semibold mb-2 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 dark:bg-primary-fixed/20 text-primary dark:text-primary-fixed text-xs font-bold">1</span>
                        <span>Primary Practice Area</span>
                      </span>
                      <span className="text-xs font-normal text-gray-500 dark:text-slate-400">Step 1 of 2 (5 Core Areas)</span>
                    </label>
                    <select
                      id="primary_category"
                      name="primary_category"
                      value={selectedPrimaryCategory}
                      onChange={(e) => {
                        setSelectedPrimaryCategory(e.target.value);
                        setSelectedSpecificService("");
                      }}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-clinical-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary text-sm font-medium transition-all"
                      required
                    >
                      <option value="">-- Select Primary Practice Area (5 Main Offerings) --</option>
                      {PRACTICE_CATEGORIES.map((cat) => (
                        <option key={cat.category} value={cat.category}>
                          {cat.category}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Step 2: Specific Protocol or Service (Reveals dynamically upon selecting Step 1 if category has sub-services) */}
                  {selectedPrimaryCategory && (() => {
                    const currentCategory = PRACTICE_CATEGORIES.find((c) => c.category === selectedPrimaryCategory);
                    const subServices = currentCategory?.services || [];

                    if (subServices.length > 0) {
                      return (
                        <div className="fade-in bg-blue-50/60 dark:bg-clinical-slate-800/90 p-4 rounded-2xl border border-primary/20 dark:border-primary/30">
                          <label htmlFor="specific_service" className="block text-gray-700 dark:text-slate-200 text-sm font-semibold mb-2 flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs font-bold">2</span>
                              <span>Specific Protocol or Engagement Focus</span>
                            </span>
                            <span className="text-xs font-semibold text-accent-teal">
                              {subServices.length} Aligned Protocols
                            </span>
                          </label>
                          <select
                            id="specific_service"
                            name="specific_service"
                            value={selectedSpecificService}
                            onChange={(e) => setSelectedSpecificService(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-primary/40 dark:border-primary-fixed/40 bg-white dark:bg-clinical-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary text-sm font-medium transition-all shadow-sm"
                            required
                          >
                            <option value="">-- Select Specific Protocol ({subServices.length} Available) --</option>
                            {subServices.map((srv) => (
                              <option key={srv} value={srv}>
                                {srv}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }

                    return (
                      <div className="fade-in flex items-center gap-2.5 px-4 py-3 rounded-xl bg-blue-50/70 dark:bg-clinical-slate-800/60 border border-primary/20 text-xs text-primary dark:text-primary-fixed font-medium">
                        <span className="material-symbols-outlined text-[18px]">verified</span>
                        <span>Direct Engagement Focus: {selectedPrimaryCategory}</span>
                      </div>
                    );
                  })()}

                  {/* Hidden input ensuring 100% compatibility with Cloudflare Worker and EmailJS */}
                  <input
                    type="hidden"
                    name="service_of_interest"
                    value={
                      selectedSpecificService
                        ? `${selectedSpecificService} (${selectedPrimaryCategory})`
                        : selectedPrimaryCategory
                    }
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-700 dark:text-slate-200 text-sm font-semibold mb-2">
                    Engagement Overview or Context
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-clinical-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    placeholder="Briefly describe the clinical or operational scenario requiring mediation or advisory..."
                    required
                  ></textarea>
                </div>

                <div className="text-center pt-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-3.5 rounded-full bg-primary hover:bg-primary-container text-white font-bold text-base shadow-lg hover:scale-105 active:scale-95 transition-all"
                    disabled={isContactSubmitting}
                  >
                    <span>{isContactSubmitting ? "Transmitting Privileged Inquiry..." : "Submit Confidential Briefing Request"}</span>
                    <span className="material-symbols-outlined text-[18px]">lock</span>
                  </button>

                  {contactSuccess && (
                    <div className="mt-6 p-4 rounded-2xl bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 text-sm">
                      <p className="font-bold">Thank you for your confidential inquiry.</p>
                      <p className="mt-1 text-xs">A senior advisor will review your briefing and contact you via secure channels within 24 business hours.</p>
                    </div>
                  )}

                  {contactError && (
                    <p className="text-red-600 text-xs mt-3">{contactError}</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* Stitch Design System Footer */}
      <FooterSection />

      {/* Interactive Detail Modals */}
      {selectedTierId && (
        <TierDetailModal
          tierId={selectedTierId}
          onClose={() => setSelectedTierId(null)}
          onScheduleClick={scrollToContact}
        />
      )}

      {selectedService && (
        <ServiceDetailModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
          onScheduleClick={scrollToContact}
        />
      )}

      {selectedInsight && (
        <InsightDetailModal
          insight={selectedInsight}
          onClose={() => setSelectedInsight(null)}
          onScheduleClick={scrollToContact}
        />
      )}
    </div>
  );
}

export default App;
